import React, { useState, useEffect } from "react"
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom"
import logo from "../../assets/images/pdflogo.png"

const navLinks = [
  { label: "HOME", path: "/" },
  { label: "PROPERTIES", path: "/properties" },
  { label: "BUYER", path: "/buyer" },
  { label: "SELLER", path: "/seller" },
  { label: "BROKER", path: "/broker" },
  { label: "LIVE AUCTION", path: "/live-auction" },
  { label: "PRICING", path: "/subscription" },
]

const isNavLinkActive = (pathname: string, path: string) => {
  if (path === "/") return pathname === "/"
  return pathname === path || pathname.startsWith(`${path}/`)
}

interface SoldxHeaderProps {
  solid?: boolean
}

const SoldxHeader: React.FC<SoldxHeaderProps> = ({ solid = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  let user: { fullName?: string; username?: string; email?: string } | null = null
  try {
    const authUserStr = localStorage.getItem("authUser")
    if (authUserStr) user = JSON.parse(authUserStr)
  } catch {
    user = null
  }
  const isLoggedIn = !!user
  const userName = user?.fullName || user?.username || user?.email || "Account"

  const closeMobileMenu = () => setMobileMenuOpen(false)
  const handleMobileNavClick = (path: string) => {
    closeMobileMenu()
    navigate(path)
  }

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  return (
    <>
      <header
        className="soldx-header"
        style={
          solid
            ? {
              position: "relative",
              background: "linear-gradient(135deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 100%)",
            }
            : undefined
        }
      >
        <div className="soldx-header-inner">
          <Link to="/" className="soldx-logo">
            <img src={logo} alt="SoldX" height={45} />
          </Link>
          <nav className="soldx-nav d-none d-lg-flex">
            {navLinks.map((link) => (
              <NavLink
                key={link.label}
                to={link.path}
                end={link.path === "/"}
                className={({ isActive }) =>
                  `soldx-nav-link${isActive ? " soldx-nav-link--active" : ""}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="soldx-header-actions">
            {isLoggedIn ? (
              <div className="soldx-profile-dropdown-wrap">
                <Link to="/dashboard" className="soldx-profile-trigger" aria-label="Profile" aria-haspopup="true">
                  <span className="soldx-profile-trigger-name">
                    <i className="mdi mdi-account soldx-profile-trigger-icon" /> {userName}
                  </span>
                </Link>
                <div className="soldx-profile-dropdown" role="menu">
                  <Link to="/dashboard" className="soldx-profile-dropdown-item" role="menuitem">
                    <i className="mdi mdi-view-dashboard" />
                    Dashboard
                  </Link>
                  <Link to="/profile" className="soldx-profile-dropdown-item" role="menuitem">
                    <i className="mdi mdi-account-outline" />
                    Profile
                  </Link>
                  <Link to="/logout" className="soldx-profile-dropdown-item soldx-profile-dropdown-item--danger" role="menuitem">
                    <i className="mdi mdi-logout" />
                    Logout
                  </Link>
                </div>
              </div>
            ) : (
              <Link to="/login" className="soldx-nav-link soldx-nav-login d-none d-sm-inline-block">
                LOGIN / SIGN UP
              </Link>
            )}
            <button
              type="button"
              className="soldx-icon-btn d-lg-none"
              aria-label="Menu"
              onClick={() => setMobileMenuOpen(true)}
            >
              <i className="mdi mdi-menu" />
            </button>
          </div>
        </div>
      </header>

      {/* Lightweight Mobile Menu */}
      <div
        className={`mobile-menu-backdrop ${mobileMenuOpen ? "open" : ""}`}
        onClick={closeMobileMenu}
      />
      <div className={`mobile-menu-panel ${mobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-header">
          <span className="mobile-menu-title">Menu</span>
          <button
            type="button"
            className="mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <i className="mdi mdi-close" />
          </button>
        </div>
        <nav className="mobile-menu-nav">
          {navLinks.map((link) => (
            <button
              key={link.label}
              type="button"
              className={`mobile-menu-link${isNavLinkActive(pathname, link.path) ? " mobile-menu-link--active" : ""}`}
              onClick={() => handleMobileNavClick(link.path)}
            >
              {link.label}
            </button>
          ))}
        </nav>
        <div className="mobile-menu-footer">
          {isLoggedIn ? (
            <>
              <button
                type="button"
                className="mobile-menu-link mobile-menu-link--profile"
                onClick={() => handleMobileNavClick("/dashboard")}
              >
                <i className="mdi mdi-view-dashboard" /> Dashboard
              </button>
              <button
                type="button"
                className="mobile-menu-link"
                onClick={() => handleMobileNavClick("/profile")}
              >
                <i className="mdi mdi-account-outline" /> Profile
              </button>
              <button
                type="button"
                className="mobile-menu-link mobile-menu-link--danger"
                onClick={() => handleMobileNavClick("/logout")}
              >
                <i className="mdi mdi-logout" /> Logout
              </button>
            </>
          ) : (
            <button
              type="button"
              className="mobile-menu-link mobile-menu-link--login"
              onClick={() => handleMobileNavClick("/login")}
            >
              LOGIN / SIGN UP
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export default SoldxHeader
